import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bot } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { SectionGuard } from './SectionGuard';
import { SidebarContext } from '@/hooks/useSidebar';
import { spacing } from '@/design/tokens';
import { AiChatPanel } from '@/sections/ai-chat/AiChatPanel';
import { useAiChatPanel } from '@/sections/ai-chat/AiChatProvider';
import { useSectionContextSync } from '@/sections/ai-chat/useSectionContext';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Root application layout.
 * Sidebar (fixed left) + main content area (scrollable).
 * Sidebar state is shared via context so the main area can adjust its margin.
 */
export function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const toggle = () => setCollapsed((c) => !c);
  const { togglePanel } = useAiChatPanel();
  const location = useLocation();
  const { canAccessSection } = useAuth();
  useSectionContextSync();

  const marginLeft = collapsed ? spacing.sidebar.collapsed : spacing.sidebar.expanded;
  const isAiChatPage = location.pathname === '/ai-chat' || location.pathname.startsWith('/ai-chat/');
  const canUseAiChat = canAccessSection('ai_chat');

  return (
    <SidebarContext.Provider value={{ collapsed, toggle }}>
      <div className="min-h-screen bg-surface-50">
        <Sidebar />

        <motion.main
          className="min-h-screen"
          animate={{ marginLeft }}
          transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
        >
          <SectionGuard>
            <Outlet />
          </SectionGuard>
        </motion.main>

        {/* Floating AI Chat toggle — hidden on dedicated AI Chat page */}
        {canUseAiChat && !isAiChatPage && (
          <button
            onClick={togglePanel}
            className="fixed bottom-6 right-6 z-30 w-12 h-12 rounded-full bg-brand-600 text-white shadow-lg hover:bg-brand-700 hover:shadow-xl active:scale-95 transition-all flex items-center justify-center"
            aria-label="Open AI Assistant"
          >
            <Bot className="w-5 h-5" />
          </button>
        )}

        {canUseAiChat && <AiChatPanel />}
      </div>
    </SidebarContext.Provider>
  );
}
